import React from "react";
import { Box, Typography, Paper, Grid, styled, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const GuideContainer = styled(Box)(({ theme }) => ({
  padding: "40px",
  backgroundColor: "#f8f9fa",
  minHeight: "100vh",
  [theme.breakpoints.down("md")]: {
    padding: "20px",
  },
}));

const GuideTitle = styled(Typography)(({ theme }) => ({
  textAlign: "center",
  fontFamily: "Inter",
  fontWeight: 700,
  fontSize: "48px",
  color: "#14582D",
  marginBottom: "20px",
  [theme.breakpoints.down("md")]: {
    fontSize: "32px",
  },
}));

const GuideSubtitle = styled(Typography)(({ theme }) => ({
  textAlign: "center",
  fontFamily: "Inter",
  fontWeight: 400,
  fontSize: "18px",
  color: "#5a5d63",
  marginBottom: "60px",
  maxWidth: "800px",
  margin: "0 auto 60px auto",
  [theme.breakpoints.down("md")]: {
    fontSize: "16px",
    marginBottom: "40px",
  },
}));

const SectionPaper = styled(Paper)(({ theme }) => ({
  padding: "30px",
  marginBottom: "30px",
  borderRadius: "15px",
  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
}));

const CodeBlock = styled(Box)(({ theme }) => ({
  backgroundColor: "#f4f4f4",
  borderRadius: "8px",
  padding: "15px",
  fontFamily: "monospace",
  fontSize: "14px",
  overflow: "auto",
  border: "1px solid #e0e0e0",
  marginTop: "10px",
}));

const FeatureGrid = styled(Grid)(({ theme }) => ({
  marginTop: "20px",
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  padding: "20px",
  textAlign: "center",
  height: "100%",
  backgroundColor: "#ffffff",
  borderRadius: "12px",
  border: "2px solid #e8f5e8",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: "#29BC62",
    transform: "translateY(-2px)",
    boxShadow: "0 8px 25px rgba(41, 188, 98, 0.15)",
  },
}));

const AnimationGuide: React.FC = () => {
  const animationTypes = [
    {
      name: "TypewriterText",
      description: "Classic typewriter effect with blinking cursor",
      useCase: "Hero titles, announcements, dramatic reveals",
      example: `<TypewriterText
  text="Your Health Journey Starts Here"
  speed={100}
  delay={0.5}
  trigger={isVisible}
/>`,
      props: [
        "text: string - The text to type out",
        "speed?: number - Typing speed in ms (default: 100)",
        "delay?: number - Delay before starting (default: 0)",
        "showCursor?: boolean - Show blinking cursor (default: true)",
        "cursorChar?: string - Cursor character (default: '|')",
        "trigger?: boolean - Start animation (default: true)"
      ]
    },
    {
      name: "StaggeredText",
      description: "Text appears letter by letter or word by word with staggered timing",
      useCase: "Headings, important announcements, smooth reveals",
      example: `<StaggeredText
  text="Achieve your health goals"
  staggerBy="words"
  staggerDelay={0.15}
  trigger={isVisible}
/>`,
      props: [
        "text: string - The text to animate",
        "staggerBy?: 'letters' | 'words' - Animation unit (default: 'letters')",
        "staggerDelay?: number - Delay between units (default: 0.05)",
        "delay?: number - Initial delay (default: 0)",
        "trigger?: boolean - Start animation (default: true)"
      ]
    },
    {
      name: "BouncingText",
      description: "Playful bounce-in animation with elastic feel",
      useCase: "Call-to-action buttons, success messages, playful content",
      example: `<BouncingText
  delay={0.5}
  trigger={isVisible}
>
  Success!
</BouncingText>`,
      props: [
        "children: ReactNode - The content to animate",
        "delay?: number - Animation delay (default: 0)",
        "trigger?: boolean - Start animation (default: true)"
      ]
    },
    {
      name: "WaveText",
      description: "Letters float up and down in a wave pattern",
      useCase: "Fun headings, loading states, playful branding",
      example: `<WaveText
  text="Wave Effect!"
  waveDelay={0.1}
  trigger={isVisible}
/>`,
      props: [
        "text: string - The text to animate",
        "waveDelay?: number - Delay between letter waves (default: 0.1)",
        "delay?: number - Initial delay (default: 0)",
        "trigger?: boolean - Start animation (default: true)"
      ]
    },
    {
      name: "ShimmerText",
      description: "Gradient color animation that shimmers across text",
      useCase: "Premium features, highlights, attention-grabbing elements",
      example: `<ShimmerText
  delay={0.5}
  trigger={isVisible}
>
  Premium Feature
</ShimmerText>`,
      props: [
        "children: ReactNode - The content to animate",
        "delay?: number - Animation delay (default: 0)",
        "trigger?: boolean - Start animation (default: true)"
      ]
    },
    {
      name: "CountUpText",
      description: "Animated number counting from start to end value",
      useCase: "Statistics, achievements, metrics, progress indicators",
      example: `<CountUpText
  start={0}
  end={10000}
  duration={2000}
  suffix="+"
  trigger={isVisible}
/>`,
      props: [
        "start?: number - Starting number (default: 0)",
        "end: number - Target number",
        "duration?: number - Animation duration in ms (default: 2000)",
        "prefix?: string - Text before number",
        "suffix?: string - Text after number",
        "trigger?: boolean - Start animation (default: true)"
      ]
    },
    {
      name: "RotatingText",
      description: "Cycles through multiple text options with smooth transitions",
      useCase: "Feature highlights, rotating testimonials, dynamic content",
      example: `<RotatingText
  texts={["Fast", "Healthy", "Delicious"]}
  interval={3000}
  trigger={isVisible}
/>`,
      props: [
        "texts: string[] - Array of texts to rotate",
        "interval?: number - Time between rotations in ms (default: 3000)",
        "delay?: number - Initial delay (default: 0)",
        "trigger?: boolean - Start animation (default: true)"
      ]
    }
  ];

  const implementationSteps = [
    {
      title: "1. Import the Components",
      description: "Import the text animation components you want to use",
      code: `import {
  TypewriterText,
  StaggeredText,
  BouncingText,
  useTextAnimation
} from '../components/Common/TextAnimations';`
    },
    {
      title: "2. Set Up Intersection Observer (Optional)",
      description: "Use the hook to trigger animations when elements come into view",
      code: `const { elementRef, isVisible } = useTextAnimation(0.2);

// Then use in your JSX:
<div ref={elementRef}>
  <TypewriterText
    text="Animate on scroll"
    trigger={isVisible}
  />
</div>`
    },
    {
      title: "3. Apply to Your Content",
      description: "Replace static text with animated components",
      code: `// Before:
<Typography variant="h1">
  Your Health Journey Starts Here
</Typography>

// After:
<TypewriterText
  text="Your Health Journey Starts Here"
  speed={80}
  trigger={isVisible}
  component={Typography}
  sx={{ variant: 'h1' }}
/>`
    }
  ];

  const bestPractices = [
    {
      title: "Performance",
      tips: [
        "Use CSS-based animations for better performance",
        "Implement intersection observers to only animate visible content",
        "Consider reduced-motion preferences for accessibility",
        "Avoid animating too many elements simultaneously"
      ]
    },
    {
      title: "User Experience",
      tips: [
        "Keep animation durations reasonable (0.3s - 2s for most effects)",
        "Use animations to guide attention, not distract",
        "Ensure text remains readable during animations",
        "Provide skip options for longer animations"
      ]
    },
    {
      title: "Accessibility",
      tips: [
        "Respect prefers-reduced-motion media query",
        "Ensure sufficient color contrast in animated text",
        "Provide alternative content for screen readers",
        "Don't rely solely on animation to convey information"
      ]
    },
    {
      title: "Design",
      tips: [
        "Use consistent animation timing across your site",
        "Match animation style to your brand personality",
        "Consider the emotional impact of different animation types",
        "Test animations on various devices and screen sizes"
      ]
    }
  ];

  return (
    <GuideContainer>
      <GuideTitle>Text Animation Guide</GuideTitle>
      <GuideSubtitle>
        Complete documentation for implementing engaging text animations in your React application.
        Each animation is designed to enhance user experience while maintaining performance and accessibility.
      </GuideSubtitle>

      {/* Quick Start */}
      <SectionPaper>
        <Typography variant="h4" sx={{ fontWeight: 600, marginBottom: "20px", color: "#14582D" }}>
          Quick Start
        </Typography>
        <Typography variant="body1" sx={{ marginBottom: "20px", color: "#5a5d63" }}>
          Get started with text animations in just a few simple steps:
        </Typography>

        {implementationSteps.map((step, index) => (
          <Accordion key={index} sx={{ marginBottom: "10px" }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {step.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" sx={{ marginBottom: "15px" }}>
                {step.description}
              </Typography>
              <CodeBlock>{step.code}</CodeBlock>
            </AccordionDetails>
          </Accordion>
        ))}
      </SectionPaper>

      {/* Available Animations */}
      <SectionPaper>
        <Typography variant="h4" sx={{ fontWeight: 600, marginBottom: "20px", color: "#14582D" }}>
          Available Animations
        </Typography>

        <FeatureGrid container spacing={3}>
          {animationTypes.map((animation, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <FeatureCard>
                <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: "10px", color: "#29BC62" }}>
                  {animation.name}
                </Typography>
                <Typography variant="body2" sx={{ marginBottom: "15px", color: "#5a5d63" }}>
                  {animation.description}
                </Typography>
                <Typography variant="caption" sx={{
                  backgroundColor: "#e8f5e8",
                  padding: "5px 10px",
                  borderRadius: "12px",
                  color: "#14582D",
                  fontWeight: 500
                }}>
                  {animation.useCase}
                </Typography>

                <Accordion sx={{ marginTop: "15px" }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Example & Props
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <CodeBlock sx={{ fontSize: "12px" }}>
                      {animation.example}
                    </CodeBlock>
                    <Box sx={{ marginTop: "15px" }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: "block", marginBottom: "8px" }}>
                        Available Props:
                      </Typography>
                      {animation.props.map((prop, propIndex) => (
                        <Typography key={propIndex} variant="caption" sx={{
                          display: "block",
                          fontSize: "11px",
                          color: "#5a5d63",
                          marginBottom: "3px",
                          fontFamily: "monospace"
                        }}>
                          • {prop}
                        </Typography>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </FeatureCard>
            </Grid>
          ))}
        </FeatureGrid>
      </SectionPaper>

      {/* Best Practices */}
      <SectionPaper>
        <Typography variant="h4" sx={{ fontWeight: 600, marginBottom: "20px", color: "#14582D" }}>
          Best Practices
        </Typography>

        <Grid container spacing={3}>
          {bestPractices.map((category, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Box sx={{ padding: "20px", backgroundColor: "#f8f9fa", borderRadius: "12px" }}>
                <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: "15px", color: "#29BC62" }}>
                  {category.title}
                </Typography>
                {category.tips.map((tip, tipIndex) => (
                  <Typography key={tipIndex} variant="body2" sx={{
                    marginBottom: "8px",
                    color: "#5a5d63",
                    display: "flex",
                    alignItems: "flex-start"
                  }}>
                    <span style={{ marginRight: "8px", color: "#29BC62" }}>•</span>
                    {tip}
                  </Typography>
                ))}
              </Box>
            </Grid>
          ))}
        </Grid>
      </SectionPaper>

      {/* Integration Examples */}
      <SectionPaper>
        <Typography variant="h4" sx={{ fontWeight: 600, marginBottom: "20px", color: "#14582D" }}>
          Integration with Existing Components
        </Typography>

        <Typography variant="body1" sx={{ marginBottom: "20px", color: "#5a5d63" }}>
          Here's how to integrate text animations with your existing Homepage sections:
        </Typography>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Hero Section Enhancement
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <CodeBlock>
{`// Enhanced Hero with Typewriter Effect
import { TypewriterText, StaggeredText } from './TextAnimations';

const EnhancedHero = () => {
  const { elementRef, isVisible } = useTextAnimation();

  return (
    <HeroSection ref={elementRef}>
      <TypewriterText
        text="Your Health Journey Starts Here"
        speed={80}
        trigger={isVisible}
        component={HeroTitle}
      />
      <StaggeredText
        text="Achieve your health goals with guidance tailored to your lifestyle"
        staggerBy="words"
        delay={2}
        trigger={isVisible}
        component={HeroSubtitle}
      />
    </HeroSection>
  );
};`}
            </CodeBlock>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Statistics Section with Count-Up
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <CodeBlock>
{`// Statistics with animated numbers
import { CountUpText } from './SpecializedTextAnimations';

const StatsSection = () => {
  const { elementRef, isVisible } = useTextAnimation();

  return (
    <Box ref={elementRef}>
      <CountUpText
        end={10000}
        suffix="+"
        trigger={isVisible}
        component={StatNumber}
      />
      <Typography>Happy Customers</Typography>
    </Box>
  );
};`}
            </CodeBlock>
          </AccordionDetails>
        </Accordion>
      </SectionPaper>
    </GuideContainer>
  );
};

export default AnimationGuide;